import { Injectable } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs';
import { AuthorizeService } from 'app/services/authorize/authorize.service';
import { UserService } from 'app/services/user/user.service';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import * as Cookies from 'js-cookie/src/js.cookie.js';
import * as moment from 'moment';
import { CommunicationTemplate, CompanyCommunication } from 'app/model/user/communication-preference.model';
import { DialogCompanyCommunicationTextComponent } from 'app/components/modal/dialog-company-communication-text/dialog-company-communication-text.component';
import { DialogCompanyCommunicationBannerComponent } from 'app/components/modal/dialog-company-communication-banner/dialog-company-communication-banner.component';
import { DialogTermsAndConditionsComponent } from 'app/components/modal/dialog-terms-and-conditions/dialog-terms-and-conditions.component';
import { consentName, domain } from 'environments/properties';
import { Consent, ConsentType } from 'app/model/user/consent-model';
import { FeatureNotifyModel } from 'app/model/user.model';
import { BannerService } from 'app/services/banner/banner.service';
import { DialogBannerOverlayComponent } from 'app/components/modal/dialog-banner-overlay/dialog-banner-overlay.component';
import { FeatureType } from 'app/model/constant';
import { Router } from '@angular/router';
import { PinService } from '../pin/pin.service';

export enum NotifyType {
  CONSENT,
  COMMUNICATION,
  BANNER_OVERLAY,
  SET_PIN
}

@Injectable()
export class NoticeService {

  private checkedConsent = false;

  constructor(
    private authorizeService: AuthorizeService,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private utilitiesService: UtilitiesService,
    private bannerService: BannerService,
    private dialog: MdDialog,
    private router: Router,
    private pinService: PinService
  ) { }

  openDialogNotifyByType(notifyType: NotifyType[]): Observable<any> {
    return Observable.of(true)
      .flatMap(() => {
        if (notifyType.includes(NotifyType.CONSENT)) {
          return this.openDialogConsent();
        }
        return Observable.of(false);
      })
      .flatMap(() => {
        if (notifyType.includes(NotifyType.BANNER_OVERLAY)) {
          return this.openDialogBannerOverlay();
        }
        return Observable.of(false);
      })
      .flatMap((closedBanner) => {
        if (closedBanner) {
          Cookies.set(AppConfigService.bannerClosedCookieName, moment().unix(), { expires: 1, domain: domain });
        }
        return Observable.of(closedBanner);
      })
      .flatMap(() => {
        if (notifyType.includes(NotifyType.COMMUNICATION)) {
          return this.openDialogCompanyCommunication();
        }
        return Observable.of(false);
      })
      .flatMap((closedCommunication) => {
        if (closedCommunication) {
          this.setNotifyDisabled(FeatureType.CONFIG_NOTIFY_TERMS_CONDITIONS_AFTER_LOGIN, true);
        }
        return Observable.of(closedCommunication);
      }).flatMap(() => {
        if (notifyType.includes(NotifyType.SET_PIN)) {
          const isEnablePIN = this.userService.getUserFeature(FeatureType.ENABLE_PIN) && this.userService.getUserFeature(FeatureType.ENABLE_PIN).allow;
          const isAllowCreditTerm = this.userService.getUserFeature(FeatureType.ALLOW_CREDIT_TERMS) && this.userService.getUserFeature(FeatureType.ALLOW_CREDIT_TERMS).allow;
          if (isEnablePIN && isAllowCreditTerm) {
            const { userId } = this.userService.getUser();
            this.pinService.userPINExists(userId).subscribe((res) => {
              if (!res.exists) {
                this.router.navigateByUrl("/setting?set_pin=true")
              }
            })
          }
        }
        return Observable.of(false);
      })
  }

  openDialogConsent(): Observable<any> {
    if (!this.checkedConsent) {
      const { userId } = this.userService.getUser();
      return this.userService.getUserConsentDetail(userId, consentName, [ConsentType.TERMS, ConsentType.PRIVACY].join(','), this.appConfigService.getLanguage())
        .flatMap((res) => {
          this.checkedConsent = true;
          if (res && !res.accepted) {
            const dialogRef: MdDialogRef<DialogTermsAndConditionsComponent> = this.dialog.open(DialogTermsAndConditionsComponent, new MdDialogConfig());
            dialogRef.componentInstance.userId = userId;
            dialogRef.componentInstance.consent = res;
            dialogRef.componentInstance.isShowSubmit = true;
            return dialogRef.afterClosed();
          }
          return Observable.of(false);
        })
        .catch(() => Observable.of(false));
    }
    return Observable.of(false);
  }

  openDialogCompanyCommunication(): Observable<any> {
    const hasNotifyDisabledCookie = !!Cookies.get(AppConfigService.notifyDisabledCookieName);
    if (!hasNotifyDisabledCookie) {
      const { companyId } = this.userService.getUser();
      return this.authorizeService.getCommunicationPreference(companyId, this.appConfigService.getLanguage(), this.utilitiesService.isMobile())
        .flatMap((res) => {
          if (res && res.display) {
            switch (res.template) {
              case CommunicationTemplate.TEXT:
                return this.openDialogCompanyCommunicationTextTemplate(res);
              case CommunicationTemplate.BANNER:
                return this.openDialogCompanyCommunicationBannerTemplate(companyId, res);
            }
          }
          return Observable.of(false);
        })
        .catch(() => Observable.of(false));
    }
    return Observable.of(false);
  }

  private openDialogCompanyCommunicationTextTemplate(data: CompanyCommunication): Observable<any> {
    const config = new MdDialogConfig();
    config.data = data;
    config.disableClose = true;
    return this.dialog.open(DialogCompanyCommunicationTextComponent, config).afterClosed();
  }

  private openDialogCompanyCommunicationBannerTemplate(companyId: number, data: CompanyCommunication): Observable<any> {
    return this.authorizeService.getCommunicationPreferenceBanner(companyId, data.bannerTemplate.bannerId)
      .flatMap((res) => {
        data.bannerTemplate.bannerImage = res;
        const config = new MdDialogConfig();
        config.data = data;
        config.disableClose = true;
        return this.dialog.open(DialogCompanyCommunicationBannerComponent, config).afterClosed();
      });
  }

  removeNotifyCookie() {
    Cookies.remove(AppConfigService.notifyDisabledCookieName, { domain: domain });
  }

  setNotifyDisabled(notifyType: String, disabled: boolean) {
    const notifyCookie = Cookies.get(AppConfigService.notifyDisabledCookieName);
    if (notifyCookie) {
      const notifyCookieDecoded = JSON.parse(this.utilitiesService.decodeFromCookie(notifyCookie));
      const notifyCookieFound = notifyCookieDecoded.find(feature => (feature.feature === notifyType));
      if (notifyCookieFound) {
        notifyCookieFound.disabled = disabled;
        Cookies.set(AppConfigService.notifyDisabledCookieName,
          this.utilitiesService.encodeToCookie(JSON.stringify(notifyCookieDecoded)),
          { expires: 1, domain: domain });
      } else {
        notifyCookieDecoded.push({ feature: notifyType, disabled: disabled });
        Cookies.set(AppConfigService.notifyDisabledCookieName,
          this.utilitiesService.encodeToCookie(JSON.stringify(notifyCookieDecoded)),
          { expires: 1, domain: domain });
      }
    } else {
      Cookies.set(
        AppConfigService.notifyDisabledCookieName,
        this.utilitiesService.encodeToCookie(JSON.stringify([{ feature: notifyType, disabled: disabled }])),
        { expires: 1, domain: domain }
      );
    }
  }

  displayNotifyDialog(notifyType: String) {
    const notifyCookie = Cookies.get(AppConfigService.notifyDisabledCookieName);
    if (!notifyCookie) {
      return true;
    }
    const featureCookieDecoded = <FeatureNotifyModel[]>JSON.parse(this.utilitiesService.decodeFromCookie(notifyCookie));
    const notifyTermsCondition = featureCookieDecoded ? featureCookieDecoded.find(feature => (feature.feature === notifyType)) : null;

    return notifyTermsCondition && notifyTermsCondition.data && notifyTermsCondition.data.display && !notifyTermsCondition.disabled;
  }

  openDialogBannerOverlay(): Observable<any> {
    const closedOn = Cookies.get(AppConfigService.bannerClosedCookieName);
    if (closedOn && moment.unix(closedOn).isSame(moment(), 'day')) {
      return Observable.of(false);
    }

    const isMobile = this.utilitiesService.isMobile();
    const device = isMobile ? 'mobile' : 'desktop';
    const { companyId } = this.userService.getUser();
    return this.bannerService.getBannerOverlay(companyId, this.appConfigService.getLanguage(), device)
      .flatMap((res) => {
        if (res && res.length > 0) {
          return this.dialog.open(DialogBannerOverlayComponent, {
            data: res,
            disableClose: true
          }).afterClosed();
        }
        return Observable.of(false);
      })
      .catch(() => Observable.of(false));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/notice/notice.service.ts